// src/utils/liveHistory.js
// Two-tier history retention + slim-snapshot helpers.
//
// Moved VERBATIM out of pages/MainApp.js so the app-level live-traffic engine
// (context/TrafficContext.js) owns the same retention/persistence logic that the
// Dashboard used to. Semantics are unchanged — the only adaptation is that
// buildSlimSnapshot now takes the "already-stashed" Set as an argument instead of
// closing over a component ref.

import { stashBodies } from '../storage/db';

// ── Two-tier history retention (mirrors the proxy) ───────────────────────────
// Keep full request/response bodies for the most recent FULL_BODY_KEEP rows per
// device; older rows keep metadata only (bodies stripped) up to HISTORY_KEEP. This
// lets the dashboard hold a long session without truncating at 500 (the old cap) or
// ballooning browser memory. Click a recent row for raw bytes; older rows are
// metadata-only (the proxy/HAR store has their full bodies if needed) — the detail
// pane labels those as "trimmed to save memory" (see utils/bodyState.js) so a
// trimmed body is never confused with a real capture miss.
export const FULL_BODY_KEEP = 500;
export const HISTORY_KEEP = 3000;

export const stripBodies = (entry) => {
    if (!entry) return entry;
    // Omit the heavy body fields (bound to ignored names) — keep everything else.
    const { requestBody: _rb, responseBody: _rp, requestBodyParsed: _rbp, ...rest } = entry;
    return { ...rest, _bodyStripped: true };
};

// rows are newest-first: cap at HISTORY_KEEP and strip bodies beyond FULL_BODY_KEEP.
export const applyTwoTier = (rows) => {
    const capped = rows.slice(0, HISTORY_KEEP); // slice always copies — safe to reassign elements
    for (let i = FULL_BODY_KEEP; i < capped.length; i++) {
        if (capped[i] && !capped[i]._bodyStripped) capped[i] = stripBodies(capped[i]);
    }
    return capped;
};

/* -------------------- Lightweight persistence helpers -------------------- */
export const SLIM_KEEP_PER_GROUP = 500; // Full session backup — bodies stripped by stripHeavy so ~2-3KB/entry
function stripHeavy(entry) {
    if (!entry) return {};
    // Strip large bodies — keeps validData, issues, url, timing, device fields
    // Each stripped entry is ~2-3KB so 500 entries ≈ 1-1.5MB (well within 5MB localStorage limit)
    // Mark `_bodyStripped` (like stripBodies) so the detail pane reports these
    // restored-from-backup rows as "trimmed to save memory" rather than the
    // misleading "body not captured — gzip/QUIC/capture miss" (see utils/bodyState.js).
    const { requestBody: _rb, responseBody: _rp, requestBodyParsed: _rbp, ...rest } = entry;
    return { ...rest, _bodyStripped: true };
}

// ── Snapshot reconciliation (fixes the "phantom device count" bug) ──────────
// A non-empty history_snapshot from the proxy is its COMPLETE retained history.
// The old merge only ever UNIONED existing rows with the snapshot and never
// pruned — so a device group cached in the browser (`groupedResults.slim`)
// could survive Clear / logout-login / proxy restarts forever, even after the
// proxy's own history had zero rows for it (the visible symptom: a device tab
// like "Android Mobile (2999)" with a stale, permanently-stuck count).
//
// Rule: for a NON-EMPTY snapshot, any device key present in `existing` but
// with ZERO rows in the snapshot has been cleared/evicted on the proxy — drop
// that group entirely. Devices that ARE in the snapshot keep the existing
// union + dedup-by-requestId + newest-first sort + two-tier retention. Live
// events for a genuinely-active device arrive AFTER as `<type> auction`
// messages and re-populate the group normally, so nothing is lost for a
// still-connected device.
//
// The EMPTY-snapshot short-circuit ("proxy restarted / no history file yet ->
// keep existing data") is the caller's responsibility — this helper assumes
// it is only invoked for a non-empty, already-grouped snapshot. If called with
// an empty/missing snapshot anyway, it safely no-ops (returns `existing`).
export function reconcileGroupsWithSnapshot(existing, snapshotGrouped, applyTwoTierFn = applyTwoTier) {
    try {
        const safeExisting = (existing && typeof existing === 'object') ? existing : {};
        const safeSnapshot = (snapshotGrouped && typeof snapshotGrouped === 'object') ? snapshotGrouped : null;

        if (!safeSnapshot || Object.keys(safeSnapshot).length === 0) {
            // Nothing authoritative to reconcile against — no-op (mirrors the
            // caller's empty-snapshot guard; belt-and-suspenders here too).
            return safeExisting;
        }

        const next = {};
        for (const [key, snapRows] of Object.entries(safeSnapshot)) {
            if (!Array.isArray(snapRows)) {
                // Malformed entry for this key — we can't trust it enough to prune
                // or merge, so preserve whatever this key already had rather than
                // silently dropping real data (degrade safely, per-key).
                if (safeExisting[key]) next[key] = safeExisting[key];
                continue;
            }
            const existingRows = Array.isArray(safeExisting[key]) ? safeExisting[key] : [];
            const existingIds = new Set(existingRows.map(r => r?.requestId).filter(Boolean));
            const newRows = snapRows.filter(r => r && (!r.requestId || !existingIds.has(r.requestId)));
            const merged = [...existingRows, ...newRows];

            // Coerce ts: snapshot rows carry ISO strings, live rows carry Date.now()
            // numbers — a raw subtraction on strings is NaN and silently breaks the sort.
            const tsNum = (r) => {
                const t = r && r.timestamp;
                if (typeof t === 'number') return t;
                const p = Date.parse(t);
                return Number.isNaN(p) ? 0 : p;
            };
            merged.sort((a, b) => tsNum(b) - tsNum(a));
            next[key] = applyTwoTierFn(merged);
        }
        // Any key in `safeExisting` NOT copied into `next` above is the prune:
        // it has zero rows in the snapshot, so it's dropped from the result.
        return next;
    } catch (e) {
        // Never let a malformed snapshot crash the WS handler — degrade to the
        // old union-only behavior by returning existing state untouched.
        console.warn('⚠️ reconcileGroupsWithSnapshot failed, keeping existing groups:', e);
        return (existing && typeof existing === 'object') ? existing : {};
    }
}

// grouped -> slim (bodies stripped). `stashedSet` dedups IndexedDB body stashes
// across calls (was the component `stashedRef.current` Set in MainApp) so we don't
// re-write the same large bodies on every slim-snapshot save.
export function buildSlimSnapshot(grouped, stashedSet) {
    const out = {};
    const toStash = [];
    for (const [key, list] of Object.entries(grouped || {})) {
        const top = list.slice(0, SLIM_KEEP_PER_GROUP);
        out[key] = top.map(stripHeavy);
        // Stash each row's full bodies to IndexedDB (once) before the slim
        // backup drops them — so a trimmed/reloaded row can load its body on
        // demand from the detail pane. Idempotent + fire-and-forget.
        for (const entry of top) {
            const rid = entry?.requestId || entry?.id;
            if (!rid || stashedSet.has(rid)) continue;
            const hasBody = (entry.requestBody != null && entry.requestBody !== '')
                || (entry.responseBody != null && entry.responseBody !== '');
            if (!hasBody) continue;
            stashedSet.add(rid);
            toStash.push(entry);
        }
    }
    if (toStash.length) stashBodies(toStash);
    return out;
}
